// /src/components/SSBCountryExposureReport.jsx
// Sponsor-bank grade: geo exposure KPI + top countries bars + trend (optional) + clean table
import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  LabelList,
} from "recharts";

//const API_BASE = "http://localhost:9191";
const API_BASE =
  process.env.REACT_APP_SSB_API_BASE ||
  (window.location.hostname === "localhost"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");

const money0 = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(Number(n));

const int0 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : new Intl.NumberFormat("en-US").format(Number(n));

const pct2 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(2)}%`;

const shortMonth = (m) => (m || "").slice(0, 10);

const Pill = ({ text, tone = "neutral" }) => {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
    info: { bg: "#EFF8FF", fg: "#175CD3" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 900,
        border: "1px solid rgba(0,0,0,0.04)",
        letterSpacing: 0.2,
      }}
    >
      {text}
    </span>
  );
};

const Card = ({ title, value, sub }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E7ECF3",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      minHeight: 88,
    }}
  >
    <div style={{ fontSize: 12, color: "#5E6B7A", marginBottom: 8, fontWeight: 700 }}>{title}</div>
    <div style={{ fontSize: 22, fontWeight: 900, color: "#0B1F33", lineHeight: 1.1 }}>{value}</div>
    {sub ? <div style={{ fontSize: 12, color: "#7A8696", marginTop: 6 }}>{sub}</div> : null}
  </div>
);

function buildPeriodMeta(months, periodType) {
  if (periodType === "month") {
    return months.map((m) => ({ label: shortMonth(m), months: [m] }));
  }
  const map = new Map();
  for (const m of months) {
    const year = m.slice(0, 4);
    const q = Math.ceil(parseInt(m.slice(5, 7)) / 3);
    const key = `${year}-Q${q}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  return Array.from(map.entries()).map(([label, ms]) => ({ label, months: ms }));
}

const US_VARIANTS = new Set(["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA"]);

const isUSCountry = (country) => US_VARIANTS.has(String(country || "").trim().toUpperCase());


const PERIOD_TYPES = ["month", "quarter"];

function TooltipBar({ active, payload, metric }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;

  const primary = metric === "count" ? int0(p.txn_count) : money0(p.total_amount);
  const share = metric === "count" ? pct2(p.count_share_pct) : pct2(p.amount_share_pct);

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #E7ECF3",
        borderRadius: 12,
        padding: 10,
        boxShadow: "0 16px 40px rgba(16,24,40,0.12)",
        maxWidth: 360,
      }}
    >
      <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 6 }}>{p.country || "UNKNOWN"}</div>
      <div style={{ fontSize: 13, color: "#0B1F33" }}>
        <div>{metric === "count" ? "Txn Count" : "Total Volume"}: <b>{primary}</b></div>
        <div>Share: <b>{share}</b></div>
      </div>
    </div>
  );
}

export default function SSBCountryExposureReport() {
  const [programs, setPrograms] = useState([]);
  const [months, setMonths] = useState([]);

  const [programId, setProgramId] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [periodType, setPeriodType] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const [metric, setMetric] = useState("amount"); // amount | count
  const [limit, setLimit] = useState(25);

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [report, setReport] = useState(null);
  const [trend, setTrend] = useState([]);

  const periodMeta = useMemo(() => buildPeriodMeta(months, periodType), [months, periodType]);

  // Load filters
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingFilters(true);
      setErr("");
      try {
        const resp = await fetch(`${API_BASE}/v1/ssb/filters/country-exposure`);
        const json = await resp.json();
        if (!mounted) return;
        setPrograms(Array.isArray(json.programs) ? json.programs : []);
        setMonths(Array.isArray(json.months) ? json.months : []);
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to load filters. Is the API running?");
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load products — all when no program selected, scoped when one is chosen
  useEffect(() => {
    let mounted = true;
    setLoadingProducts(true);
    (async () => {
      try {
        const url = new URL(`${API_BASE}/v1/ssb/filters/products`);
        if (programId) url.searchParams.set("program_id", programId);
        const resp = await fetch(url.toString());
        const json = await resp.json();
        if (mounted) { setProducts(Array.isArray(json.products) ? json.products : []); setProductId(""); }
      } catch (e) {
        console.error("Failed to load products:", e);
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    })();
    return () => { mounted = false; };
  }, [programId]);

  // Init selectedPeriod when periodMeta changes
  useEffect(() => {
    if (periodMeta.length > 0) {
      setSelectedPeriod((prev) => {
        const stillValid = periodMeta.some((m) => m.label === prev);
        return stillValid ? prev : periodMeta[0].label;
      });
    }
  }, [periodMeta]);

  // Load report
  useEffect(() => {
    if (!selectedPeriod || periodMeta.length === 0) return;
    const meta = periodMeta.find((m) => m.label === selectedPeriod);
    if (!meta) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        // meta.months covers only the selected period (1 for "month" view, 3 for
        // "quarter") — the backend aggregates the whole range server-side, so
        // this is a single call regardless of period type.
        const orderedMonths = meta.months.slice().sort();
        const rangeStart = orderedMonths[0];
        const rangeEnd = orderedMonths[orderedMonths.length - 1];

        const url = new URL(`${API_BASE}/v1/ssb/countries/exposure`);
        url.searchParams.set("month_start", rangeStart);
        if (rangeEnd !== rangeStart) url.searchParams.set("month_end", rangeEnd);
        url.searchParams.set("limit", String(limit));
        url.searchParams.set("order_by", metric);
        if (programId) url.searchParams.set("program_id", programId);
        if (productId) url.searchParams.set("product_id", productId);

        const resp = await fetch(url.toString());
        if (!mounted) return;
        if (!resp.ok) { setReport(null); return; }
        const combined = await resp.json();
        if (!mounted) return;
        setReport(combined);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(`Failed to load report: ${e.message || e}`);
        if (mounted) setReport(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedPeriod, periodMeta, programId, productId, limit, metric]);

  const rows = report?.rows || [];

  const summary = useMemo(() => {
    const total_amount = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    const txn_count = rows.reduce((s, r) => s + Number(r.txn_count || 0), 0);
    return { total_amount, txn_count, distinct_countries: rows.length };
  }, [report]);

  // Compute headline: US vs non-US (based on returned rows; if limit small, it's an approximation)
  const headline = useMemo(() => {
    const totalAmount = Number(summary.total_amount || 0);
    const totalCount = Number(summary.txn_count || 0);
  
    const usAmt = rows
      .filter((r) => isUSCountry(r.country))
      .reduce((s, r) => s + Number(r.total_amount || 0), 0);
  
    const usCnt = rows
      .filter((r) => isUSCountry(r.country))
      .reduce((s, r) => s + Number(r.txn_count || 0), 0);
  
    const nonUsAmt = Math.max(totalAmount - usAmt, 0);
    const nonUsCnt = Math.max(totalCount - usCnt, 0);
  
    const nonUsAmtPct =
      totalAmount > 0 ? (nonUsAmt / totalAmount) * 100 : 0;
  
    const nonUsCntPct =
      totalCount > 0 ? (nonUsCnt / totalCount) * 100 : 0;
  
    return {
      totalAmount,
      totalCount,
      nonUsAmtPct,
      nonUsCntPct,
      usAmt,
      usCnt,
    };
  }, [rows, summary.total_amount, summary.txn_count]);

  const bannerTone =
    headline.nonUsAmtPct >= 35 ? "warn" : headline.nonUsAmtPct >= 15 ? "info" : "good";

  const periodLabel = periodType === "quarter" ? "Quarter" : "Month";

  const toggleStyle = (active) => ({
    padding: "3px 9px", borderRadius: 6,
    border: `1px solid ${active ? "#3730A3" : "#D7DEE9"}`,
    background: active ? "#EEF2FF" : "#fff",
    color: active ? "#3730A3" : "#64748B",
    fontSize: 11, fontWeight: active ? 800 : 500, cursor: "pointer", lineHeight: 1.4,
  });

    const chartData = useMemo(() => {
      return rows.slice(0, 10).map((r) => ({
        country: r.country || "UNKNOWN",
        txn_count: Number(r.txn_count || 0),
        total_amount: Number(r.total_amount || 0),
        amount_share_pct: Number(r.amount_share_pct || 0),
        count_share_pct: Number(r.count_share_pct || 0),
        value: metric === "count"
          ? Number(r.count_share_pct || 0)
          : Number(r.amount_share_pct || 0),
      }));
    }, [rows, metric]);

  // Trend: non-US share over last 12 months (always monthly granularity).
  // Single call — the backend computes all 12 months' shares in one query.
  useEffect(() => {
    if (months.length === 0) { setTrend([]); return; }
    const controller = new AbortController();
    (async () => {
      try {
        const url = new URL(`${API_BASE}/v1/ssb/countries/exposure/trend`);
        url.searchParams.set("months", "12");
        if (programId) url.searchParams.set("program_id", programId);
        if (productId) url.searchParams.set("product_id", productId);
        const resp = await fetch(url.toString(), { signal: controller.signal });
        if (!resp.ok) { setTrend([]); return; }
        const json = await resp.json();
        const series = Array.isArray(json?.trend) ? json.trend : [];
        const out = series
          .map((r) => ({
            month: shortMonth(r.month_start),
            non_us_share: metric === "count" ? Number(r.non_us_count_share_pct || 0) : Number(r.non_us_amount_share_pct || 0),
          }))
          .sort((a, b) => (a.month > b.month ? 1 : -1));
        setTrend(out);
      } catch (e) {
        if (e.name !== "AbortError") console.error("Trend load failed:", e);
      }
    })();
    return () => { controller.abort(); };
  }, [programId, productId, months, metric]);

  return (
    <SSBReportShell
      title="SSB Dashboard — Country Exposure"
      subtitle={`Program: ${programs.find((p) => p.program_id === programId)?.program_name || "All Programs"}${productId ? ` › ${products.find((p) => p.product_id === productId)?.product_name || ""}` : ""} • ${periodLabel}: ${selectedPeriod || "—"}`}
      rightMeta={[
        `Metric: ${metric === "count" ? "Txn Count" : "Volume ($)"}`,
        `Top N: ${limit}`,
        loading ? "Loading…" : "Live",
      ]}
      leftMeta={[
        "Sponsor bank oversight view",
        "Jurisdiction exposure / cross-border footprint",
      ]}
      footerLeft="Idenza • SSB Reporting (Phase 1)"
      footerRight="Source: mv_ssb_reports_country_exposure_monthly"
    >
      {/* Filters */}
      <SSBFiltersBar columns={5}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Program</div>
          <select
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            disabled={loadingFilters}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="">All Programs</option>
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>{p.program_name}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Product</div>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={loadingProducts}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="">All Products</option>
            {products.map((p) => (
              <option key={p.product_id} value={p.product_id}>{p.product_name}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>Period</div>
            <div style={{ display: "flex", gap: 3 }}>
              {PERIOD_TYPES.map((t) => (
                <button key={t} onClick={() => setPeriodType(t)} style={toggleStyle(periodType === t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            disabled={loadingFilters || periodMeta.length === 0}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            {periodMeta.map((m) => (
              <option key={m.label} value={m.label}>{m.label}</option>
            ))}
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>View</div>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="amount">Volume ($)</option>
            <option value="count">Txn Count</option>
          </select>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>Top N</div>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
      </SSBFiltersBar>
  
      {/* Banner */}
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E7ECF3",
          borderRadius: 16,
          padding: 14,
          boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Pill text="Cross-Border Signal" tone={bannerTone} />
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0B1F33" }}>
              Non-US exposure: {headline.nonUsAmtPct.toFixed(1)}% of volume ({headline.nonUsCntPct.toFixed(1)}% of txns)
            </div>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>
            Total volume: <b>{money0(headline.totalAmount)}</b> • Total txns: <b>{int0(headline.totalCount)}</b> • Distinct countries: <b>{int0(summary.distinct_countries)}</b>
          </div>
        </div>
        <div>{loading ? <Pill text="Loading…" tone="neutral" /> : <Pill text="Live" tone="good" />}</div>
      </div>
  
      {/* Error */}
      {err ? (
        <div
          style={{
            background: "#FEF3F2",
            border: "1px solid #FECACA",
            color: "#7F1D1D",
            padding: 12,
            borderRadius: 16,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      ) : null}
  
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        <Card title={`Total ${periodLabel} Volume`} value={money0(summary.total_amount)} />
        <Card title="Total Transactions" value={int0(summary.txn_count)} />
        <Card title="Distinct Countries" value={int0(summary.distinct_countries)} sub="Jurisdiction footprint" />
        <Card title="Non-US Volume Share" value={pct2(headline.nonUsAmtPct)} sub="Cross-border exposure" />
      </div>
  
      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 12, marginBottom: 12 }}>
        {/* Bar chart */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E7ECF3",
            borderRadius: 16,
            padding: 12,
            boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, color: "#0B1F33" }}>
            Top 10 Countries — {metric === "count" ? "Txn Share %" : "Volume Share %"}
            </div>
            <div style={{ fontSize: 12, color: "#64748B" }}>Hover for share %</div>
          </div>
  
          <div style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 45, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
                  style={{ fontSize: 11 }}
                />
                <YAxis
                    type="category"
                    dataKey="country"
                    width={90}
                    interval={0}
                    tick={{ fontSize: 12, fontWeight: 900 }}
                  />
                <Tooltip content={<TooltipBar metric={metric} />} />
                <Bar dataKey="value">
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(v) => `${Number(v).toFixed(1)}%`}
                  style={{ fontSize: 11, fontWeight: 700 }}
                />
              </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
  
        {/* Table */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E7ECF3",
            borderRadius: 16,
            padding: 12,
            boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          }}
        >
          <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 8 }}>Details</div>
          <div style={{ overflowX: "auto", maxHeight: 500, overflowY: "auto", border: "1px solid #F1F5F9", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #E7ECF3", background: "#F8FAFC" }}>
                  <th style={{ padding: 10 }}>Country</th>
                  <th style={{ padding: 10 }}>Txns</th>
                  <th style={{ padding: 10 }}>Amount</th>
                  <th style={{ padding: 10 }}>Txn Share</th>
                  <th style={{ padding: 10 }}>Vol Share</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, limit).map((r) => {
                  const row = {
                    country: r.country || "UNKNOWN",
                    txn_count: Number(r.txn_count || 0),
                    total_amount: Number(r.total_amount || 0),
                    amount_share_pct: Number(r.amount_share_pct || 0),
                    count_share_pct: Number(r.count_share_pct || 0),
                  };
                  const tone = row.amount_share_pct >= 20 ? "warn" : row.amount_share_pct >= 8 ? "info" : "neutral";
                  return (
                    <tr key={row.country} style={{ borderBottom: "1px solid #F1F5F9" }}>
                      <td style={{ padding: 10, fontWeight: 900, color: "#0B1F33" }}>{row.country}</td>
                      <td style={{ padding: 10 }}>{int0(row.txn_count)}</td>
                      <td style={{ padding: 10 }}>{money0(row.total_amount)}</td>
                      <td style={{ padding: 10 }}><Pill text={pct2(row.count_share_pct)} tone="info" /></td>
                      <td style={{ padding: 10 }}><Pill text={pct2(row.amount_share_pct)} tone={tone} /></td>
                    </tr>
                  );
                })}
                {!loading && rows.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 12, color: "#64748B" }}>No data.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
  
          <div style={{ marginTop: 10, fontSize: 12, color: "#64748B" }}>
            Sponsor-bank value: jurisdiction exposure is a primary driver of AML oversight posture.
          </div>
        </div>
      </div>
  
      {/* Trend */}
      <div style={{ background: "#FFFFFF", border: "1px solid #E7ECF3", borderRadius: 16, padding: 12, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontWeight: 900, color: "#0B1F33" }}>Non-US Exposure Trend — {metric === "count" ? "Txn Count %" : "Volume $"} (last 12 months)</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Approximation based on top countries returned</div>
        </div>
  
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" style={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `${Number(v).toFixed(0)}%`} style={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
              <Line type="monotone" dataKey="non_us_share" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </SSBReportShell>
  );
}